import React, { useState, useEffect } from 'react';
import AppSidebar from '../../components/Sidebar';
import { useAuth } from '../../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { Client, Databases, Storage, ID, Query } from 'appwrite';

const client = new Client();
client.setEndpoint('https://cloud.appwrite.io/v1').setProject('67fbee6a001260770104');

const databases = new Databases(client);
const storage = new Storage(client);

const DATABASE_ID = '68098bc80034d68e4e64';
const COLLECTION_ID = '68098bd900226cbeb1cd';
const BUCKET_ID = '680990d6000f00f9d400';

const ADMIN_EMAIL = 'ramonsaint23@gmail.com';


const POEM_CATEGORIES = ['Lullaby', 'Festival', 'Romantic', 'Other'];
const STORY_CATEGORIES = ['Inspiration', 'Fiction', 'Non-fiction', 'Campus Tales', 'Slice of Life', 'Love & Heartbreak', 'Historical'];
const KNOWLEDGE_CATEGORIES = ['Tech Tips', 'Study Hacks', 'Life Lessons', 'Career Talk', 'Faith & Morals', 'Money Matters', 'Personal Development'];
const ESSAY_CATEGORIES = ['Argumentative', 'Descriptive', 'Narrative', 'Expository', 'Reflective'];

const ALL_CONTENT_TYPES = ['Poem', 'Story', 'Knowledge Hub', 'Scholarship', 'Essay'];

const Dashboard = () => {
  const { currentUser } = useAuth();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('');
  const [image, setImage] = useState(null);
  const [deadline, setDeadline] = useState('');
  const [poems, setPoems] = useState([]);
  const [editId, setEditId] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [contentType, setContentType] = useState('Poem');
  const [filterType, setFilterType] = useState('Poem');
  const [searchTerm, setSearchTerm] = useState('');

  const isAdmin = currentUser?.email === ADMIN_EMAIL;

  const getUserDisplayName = () => {
    if (!currentUser) return 'User';
    if (userProfile?.username) return userProfile.username;
    if (userProfile?.name) return userProfile.name;
    if (currentUser.displayName) return currentUser.displayName;
    return 'User';
  };

  const fetchUserProfile = async () => {
    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        setUserProfile(userDocSnap.data());
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const fetchPoems = async () => {
    try {
      let queries = [];
      if (!isAdmin) queries.push(Query.equal('userId', currentUser.uid));
      const response = await databases.listDocuments(DATABASE_ID, COLLECTION_ID, queries);
      const sorted = response.documents.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setPoems(sorted);
    } catch (error) {
      console.error('Error fetching content:', error);
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchUserProfile();
      fetchPoems();
    }
  }, [currentUser]);

  const getImagePreviewUrl = (fileId) => {
    try {
      return storage.getFileView(BUCKET_ID, fileId);
    } catch (error) {
      console.error('Error generating image URL:', error);
      return '';
    }
  };

  const uploadImage = async (file) => {
    try {
      const response = await storage.createFile(BUCKET_ID, ID.unique(), file);
      return response.$id;
    } catch (error) {
      console.error('Error uploading image:', error);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let imageId = null;
      if (image) imageId = await uploadImage(image);

      const newContent = {
        title,
        content,
        category,
        contentType,
        imageId,
        deadline: contentType === 'Scholarship' ? deadline : null,
        userId: currentUser.uid,
        createdAt: new Date().toISOString(),
        isApproved: isAdmin,
      };

      if (editId) {
        await databases.updateDocument(DATABASE_ID, COLLECTION_ID, editId, newContent);
        setEditId(null);
      } else {
        await databases.createDocument(DATABASE_ID, COLLECTION_ID, ID.unique(), newContent);
      }

      setTitle('');
      setContent('');
      setCategory('');
      setDeadline('');
      setImage(null);
      setContentType('Poem');
      fetchPoems();
    } catch (error) {
      console.error('Error saving content:', error);
    }
  };

  const handleEdit = (item) => {
    setTitle(item.title);
    setContent(item.content);
    setCategory(item.category || '');
    setDeadline(item.deadline || '');
    setContentType(item.contentType || 'Poem');
    setImage(null);
    setEditId(item.$id);
  };

  const handleDelete = async (id) => {
    try {
      const item = poems.find(p => p.$id === id);
      if (item?.imageId) await storage.deleteFile(BUCKET_ID, item.imageId);
      await databases.deleteDocument(DATABASE_ID, COLLECTION_ID, id);
      fetchPoems();
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const handleApprove = async (id) => {
    try {
      await databases.updateDocument(DATABASE_ID, COLLECTION_ID, id, { isApproved: true });
      fetchPoems();
    } catch (error) {
      console.error('Error approving item:', error);
    }
  };

  const getCategoryOptions = () => {
    switch (contentType) {
      case 'Story': return STORY_CATEGORIES;
      case 'Knowledge Hub': return KNOWLEDGE_CATEGORIES;
      case 'Essay': return ESSAY_CATEGORIES;
      default: return POEM_CATEGORIES;
    }
  };

  const filteredPoems = poems.filter((item) => {
    const matchesType = item.contentType === filterType;
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesType && matchesSearch;
  });

  if (!currentUser) return <div>Loading...</div>;

  

  return (
    <div className="flex">
      <div className="sticky top-0 h-screen">
        <AppSidebar user={currentUser} />
      </div>

      <div className="flex-1 ml-20 md:ml-0 p-6 bg-gray-100 min-h-screen">
        <div className="mb-4">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Hi, {getUserDisplayName()}! 👋</h1>
          <h2 className="text-xl text-gray-600">Welcome to your Dashboard</h2>
        </div>

        

        {/* Content Form */}
        <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-md mb-6">
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">Content Type</label>
            <select value={contentType} onChange={(e) => setContentType(e.target.value)} className="w-full px-3 py-2 border rounded-lg">
              {ALL_CONTENT_TYPES.map((type) => (
                <option key={type}>{type}</option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">Title</label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full px-3 py-2 border rounded-lg" required />
          </div>

          {contentType !== 'Scholarship' && (
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">Category</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full px-3 py-2 border rounded-lg" required>
                <option value="" disabled>Select a category</option>
                {getCategoryOptions().map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          )}

          {contentType === 'Scholarship' && (
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">Application Deadline</label>
              <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} className="w-full px-3 py-2 border rounded-lg" required />
            </div>
          )}

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              {contentType === 'Scholarship' ? 'Eligibility / Description' : 'Content'}
            </label>
            <textarea value={content} onChange={(e) => setContent(e.target.value)} className="w-full px-3 py-2 border rounded-lg whitespace-pre-line" rows="6" required />
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">Upload Image (optional)</label>
            <input type="file" accept="image/*" onChange={(e) => setImage(e.target.files[0])} className="w-full px-3 py-2 border rounded-lg" />
          </div>

          <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
            {editId ? `Update ${contentType}` : `Post ${contentType}`}
          </button>
        </form>


        {/* Filter and Search */}
        <div className="flex flex-wrap gap-2 mb-6">
          {ALL_CONTENT_TYPES.map((type) => (
            <button
              key={type}
              onClick={() => setFilterType(type)}
              className={`px-4 py-2 rounded-full text-sm ${
                filterType === type
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-300 text-gray-800 hover:bg-blue-500 hover:text-white'
              } transition`}
            >
              {type}
            </button>
          ))}
        </div>

        <div className="mb-6">
          <input
            type="text"
            placeholder="Search by title..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full max-w-md px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>

        {/* Filtered Content Display */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredPoems.map((item) => {
            if (!isAdmin && item.userId !== currentUser.uid) return null;

            return (
              <div key={item.$id} className="bg-white rounded-lg shadow-md overflow-hidden">
                {item.imageId && (
                  <img src={getImagePreviewUrl(item.imageId)} alt={item.title} className="h-48 w-full object-cover" onError={(e) => e.target.src = 'https://via.placeholder.com/400x200?text=Image+Not+Found'} />
                )}
                <div className="p-4">
                  <h2 className="text-xl font-bold mb-2">{item.title}</h2>
                  {item.category && (
                    <div className="mb-2">
                      <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                        {item.category}
                      </span>
                    </div>
                  )}
                  {!item.isApproved && (
                    <p className="text-red-500 text-xs mb-2">Not approved</p>
                  )}
                  {item.deadline && (
                    <p className="text-sm text-gray-600 mb-2">Deadline: {item.deadline}</p>
                  )}
                  <p className="text-gray-700 mb-4 whitespace-pre-line">{item.content}</p>
                  <div className="flex justify-between flex-wrap gap-2">
                    <button onClick={() => handleEdit(item)} className="bg-yellow-500 text-white px-4 py-2 rounded-lg">Edit</button>
                    <button onClick={() => handleDelete(item.$id)} className="bg-red-500 text-white px-4 py-2 rounded-lg">Delete</button>
                    {isAdmin && !item.isApproved && (
                      <button onClick={() => handleApprove(item.$id)} className="bg-green-600 text-white px-4 py-2 rounded-lg">Approve</button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
